import React, { useState, useEffect, useRef, useMemo } from "react";
import { useAPIDetailsContext } from "../../contexts/APIDetailsContext";
import { DAABPricingPlanTile } from "../pricing-plans/DAABPricingPlanTile";
import { IconChevronRight, IconChevronLeft, PrimaryButton } from "cdk-radial";
import { PLAN_TYPE } from "../../utils/api-pricing";

const MAX_PLANS_FOR_CAROUSEL = 5;

export const DAABApiPricing = ({
  instanceIdVersionMap,
  plans,
  isAsync,
  allEndpoints,
  addingPricingPlanToApiInCart,
  addingAPIToCart,
  isMsaConsented,
  prodInstance,
  onCompareNowClick,
  isCurrentApiInBundleCart
}) => {
  const {
    apiInfo,
    cartDetails,
    orderDetails,
    apiDetailsOnClickHandlers,
  } = useAPIDetailsContext();
  const [showArrows, setShowArrows] = useState(false);
  const [indexStart, setIndexStart] = useState(null);
  const [plansToRender, setPlansToRender] = useState([]);
  const [maxDisplay, setMaxDisplay] = useState(MAX_PLANS_FOR_CAROUSEL);

  const gridStyle =
    plans.length >= MAX_PLANS_FOR_CAROUSEL
      ? "repeat(5, 1fr)"
      : `repeat(${plans.length}, 1fr)`;
  const ref = useRef();
  const isLoading = addingPricingPlanToApiInCart || addingAPIToCart;

  const apiId = apiInfo.id;
  const isAPIContracted = apiInfo?.isContracted;
  const allOrderedAPIs = [
    ...(orderDetails?.restApis || []),
    ...(orderDetails?.asyncApis || []),
  ];
  const apiOrderItem =
    allOrderedAPIs && allOrderedAPIs.find((api) => api.id === apiId);
  const apiCartItem =
    Array.isArray(cartDetails) &&
    cartDetails.find((api) => api.resourceId === apiId);

  let isAPIPurchased = false;
  let isAPIInCart = false;
  let selectedPricingPlan;

  if (apiCartItem) {
    isAPIInCart = true;
    selectedPricingPlan = apiCartItem.resourcePricingPlanId;
  } else if (apiOrderItem) {
    isAPIPurchased = true;
    selectedPricingPlan = apiOrderItem?.integrations?.[0]?.pricingPlanId;
  }

  // Set max to display based on how many plans can fit in the container
  useEffect(() => {
    const tileWidth = 256;
    const tileMargin = 10;
    if (ref.current) {
      // width + left margin + right margin + little wiggle room
      const totalTileWidth = tileWidth + tileMargin * 2 + 2;
      setMaxDisplay(
        Math.floor((ref.current.offsetWidth - 100) / totalTileWidth)
      );
    }
  }, []);

  useEffect(() => {
    // Set arrows
    let length = plans.length || 0;
    let isMoreThanMaxPlans = length > maxDisplay ? true : false;
    setIndexStart(0);
    setShowArrows(isMoreThanMaxPlans);
  }, [maxDisplay]);

  useEffect(() => {
    let plansToRender = plans?.slice(indexStart, indexStart + maxDisplay);
    setPlansToRender(plansToRender);
  }, [indexStart, maxDisplay]);

  const handlePricingPlanSelect = (planId) => {
    apiDetailsOnClickHandlers.selectPricingPlan({
      resourceId: apiId,
      planId,
      resourceType: apiInfo?.kind,
    });
  };

  const isAdvancedPlan = plans.some(
    (plan) =>
      plan?.type?.toLowerCase() === PLAN_TYPE.ADVANCED &&
      plan?.status?.toLowerCase() === "active"
  );
  const isLeftArrowDisabled = indexStart === 0;
  const isRightArrowDisabled = indexStart + maxDisplay >= plans?.length;

  return (
    <>
      <div className="api-dir-pkg_rate-plan container">
        <div className="api-dir-pkg_rate-plan api-dir-pkg_daab-heading">
          Review and select a Pricing plan for the API.
        </div>
      </div>
      {isAdvancedPlan && prodInstance && (
        <div className="api-dir-pkg_pricing-calculator">
          <div className="api-dir-pkg_content-frame">
            <div className="api-dir-pkg_content-frame-message">
              <div className="api-dir-pkg_text-wrapper">
                Compare plans with our pricing calculator{" "}
              </div>
              <div className="api-dir-pkg_message-frame">
                Estimate your costs with the pricing calculator. See how plans compare based on your API usage.
              </div>
            </div>
            <div>
              <PrimaryButton
                iconColor="primary"
                text="Compare Now"
                onClick={onCompareNowClick}
              />
            </div>
          </div>
        </div>
      )}
      <div className="api-dir-pkg_advance-plans-container" ref={ref}>
        <div className="api-dir-pkg_advance-pricing-carousel">
          {showArrows && (
            <div className="api-dir-pkg_arrow-left">
              <button
                className="api-dir-pkg_arrow-button"
                onClick={() => {
                  setIndexStart(indexStart - maxDisplay);
                }}
              >
                {!isLeftArrowDisabled && (
                  <IconChevronLeft
                    height={24}
                    width={24}
                    data-testid="chevron-left"
                  />
                )}
              </button>
            </div>
          )}
          <div
            className={`api-dir-pkg_advance-plans${
              plans.length < MAX_PLANS_FOR_CAROUSEL ? " no-carousel" : ""
            }`}
            style={{
              gridTemplateColumns: `${gridStyle}`,
              MsGridColumns: `${gridStyle}`,
            }}
          >
            {plansToRender.map((p) => {
              const isSelected =
                !isAPIContracted && p.id === selectedPricingPlan;
              return (
                <div style={{ margin: "12px" }}>
                  <DAABPricingPlanTile
                    key={p.id}
                    plan={p}
                    isAsync={isAsync}
                    allEndpoints={allEndpoints}
                    instanceIdVersionMap={instanceIdVersionMap}
                    selectPricingPlan={handlePricingPlanSelect}
                    isSelected={isSelected}
                    isAPIinCart={isAPIInCart}
                    isAPIContracted={isAPIContracted}
                    isAPIPurchased={isAPIPurchased}
                    isLoading={isLoading}
                    isMsaConsented={isMsaConsented}
                    isCurrentApiInBundleCart={isCurrentApiInBundleCart}
                  />
                </div>
              );
            })}
          </div>
          {showArrows && (
            <div className="api-dir-pkg_arrow-right">
              <button
                className="api-dir-pkg_arrow-button"
                onClick={() => {
                  setIndexStart(indexStart + maxDisplay);
                }}
              >
                {!isRightArrowDisabled && (
                  <IconChevronRight
                    height={24}
                    width={24}
                    data-testid="chevron-right"
                  />
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};
